/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Recovery from a tab that outlived a deploy. Every build names its code-split
 * chunks by content hash (biomoleculeDefaultMenu-CdkN5LMO.js), and a deploy
 * replaces the old files. A tab opened before the deploy keeps running the OLD
 * main bundle, so the first time it needs a chunk it has not loaded yet (a
 * context menu, a project type's chat, a modal) it asks for an old file that no
 * longer exists — "Failed to fetch dynamically imported module" — and the app
 * crashed to the error page. A reload fetches the new index.html and the new
 * chunks, so that is the fix: reload once, quietly.
 *
 * Two layers call reloadForNewBuild for the same failure: the vite:preloadError
 * listener (index.tsx) and the error page (a React.lazy chunk). The first call
 * starts the reload; later calls in the same page just report "reloading" so the
 * error page shows the update note instead of an error, and nothing is reported.
 * Across reloads, at most one automatic reload per minute per tab, so a
 * genuinely missing chunk (offline, a broken deploy) cannot loop.
 */

const KEY = 'aims:staleBuildReloadAt';
const MIN_INTERVAL_MS = 60_000;
let reloading = false; // a reload was started by this page

/** A failed dynamic import / chunk preload, in the wording of Chrome, Safari and Firefox. */
export const isStaleChunkError = (message: string | undefined | null): boolean =>
  !!message &&
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
    message,
  );

/** Reload to pick up the new build; true while that reload is under way. False if one was just tried. */
export const reloadForNewBuild = (): boolean => {
  if (reloading) return true;
  // Offline (dropped school Wi-Fi), the chunk failed for lack of a network, not
  // a deploy — and a reload would land on the browser's "No internet" page.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < MIN_INTERVAL_MS) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // No sessionStorage (private mode quirks): reload anyway — a loop would
    // need the new build to be missing chunks too.
  }
  reloading = true;
  window.location.reload();
  return true;
};

/** True once this page has started a reload onto the new build. */
export const isReloadingForNewBuild = (): boolean => reloading;
